import role7Schema from '../../../../assets/forms/role-130000.form.json';
import typesDict from '../../../../assets/types.dict.json';
import {
  PureSheetValidationResult,
  SheetValidationContext,
  SheetValidator,
} from '../sheet-validator';
import {
  buildWarningBag,
  sheetHasTriggeredWarning,
  WarnableControl,
} from './warning-eval.helper';
import { evaluateConditional } from '../conditional/conditional-field.evaluator';
import { ConditionalRule, ConditionalEvalContext } from '../conditional/conditional-field.model';
import { SHEET_KEYS } from '../../../models/sheet-keys';

/**
 * Pure validator for Role 7 (Auditors Report).
 *
 * Mirrors the editor's role7-130000-auditors-report.component.ts validation
 * surface:
 *
 *  - Static required fields from schema (control.required === true).
 *  - Conditional required: fields whose schema `conditional` block is active
 *    (evaluated via the shared `evaluateConditional` pure evaluator — the same
 *    source the editor's applyConditionalRequired() and the submit projector
 *    use). This covers both:
 *      · The three ex-hideWhen fields (KAM toggle, Going-Concern toggle,
 *        PlaceOfAuditorsReport) with `inactiveWhenValueIn:["FS-MPERS","FS-MFRS"]`
 *        — active (required) when TypeOfSubmission is NOT MFRS/MPERS, including
 *        when unknown/empty (shown by default).
 *      · The two description fields (KAM / GC description) with
 *        `activeWhenValueIn:["Yes"]` on their same-sheet toggle.
 *  - Pattern validators from schema (inline `pattern` and the Role-2-style
 *    `patternSource` indirection via types.dict.json).
 *
 * The component's `updateSheetLevelStatus('auditorsReport', { hasErrors: !isValid })`
 * is the parity target; this validator never sets hasSuggestedChanges
 * (warnings are evaluated separately and do not flow through the same
 * sheet-level status publisher).
 */

interface SchemaControl {
  qname: string;
  required?: boolean;
  conditional?: ConditionalRule;
  validators?: Array<{
    name: string;
    pattern?: string;
    patternSource?: string;
  }>;
}

const SCHEMA_CONTROLS: ReadonlyArray<SchemaControl> =
  role7Schema.controls as SchemaControl[];

/**
 * Pre-compiled pattern map built from the schema's inline `pattern` and
 * `patternSource` (types.dict lookup). Matches the component's buildForm
 * branch order: patternSource wins if present, else inline pattern.
 */
const PATTERN_BY_KEY: ReadonlyMap<string, RegExp> = (() => {
  const map = new Map<string, RegExp>();
  const patterns =
    (typesDict as { patterns?: Record<string, string> }).patterns ?? {};
  for (const control of SCHEMA_CONTROLS) {
    const patternValidator = control.validators?.find(
      (validator) => validator.name === 'pattern'
    );
    if (!patternValidator) continue;

    let source: string | undefined;
    if (patternValidator.patternSource) {
      const key = patternValidator.patternSource.replace('type:', '');
      source = patterns[key];
    } else if (patternValidator.pattern) {
      source = patternValidator.pattern;
    }

    if (source) {
      try {
        map.set(control.qname, new RegExp(source));
      } catch {
        // Invalid regex — component would also fail to apply this validator,
        // so parity holds by skipping it here too.
      }
    }
  }
  return map;
})();

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * Build the evaluator context's filing-info from the two cross-sheet slices.
 * Prerequisites take precedence when present+non-empty (mirrors the editor's
 * active filing-info source); fall back to the backend `filingInfo` slice.
 * This is how cross-sheet `conditional.controlledBy:{sheet:'filingInfo',...}`
 * rules (e.g. the TypeOfSubmission-gated fields) resolve deterministically on
 * the listing path before any editor has mounted.
 */
function buildEvalContext(
  filingPrerequisites: Record<string, unknown> | undefined,
  filingInfo: Record<string, unknown> | undefined
): ConditionalEvalContext {
  const merged: Record<string, any> = { ...(filingInfo ?? {}) };
  for (const [k, v] of Object.entries(filingPrerequisites ?? {})) {
    if (v !== undefined && v !== null && v !== '') merged[k] = v;
  }
  return { filingInfo: merged };
}

/**
 * Required-field errors. A control is required when:
 *  - it carries a `conditional` whose effect includes validation/both AND the
 *    rule is currently active (evaluated via the shared evaluator — same source
 *    the editor's applyConditionalRequired() and the projector use), or
 *  - it has no conditional and is statically `required: true`.
 */
function checkRequiredErrors(
  sheetData: Record<string, any>,
  evalContext: ConditionalEvalContext
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const control of SCHEMA_CONTROLS) {
    const required = control.conditional
      ? control.conditional.appliesTo !== 'visibility' &&
        evaluateConditional(control.conditional, sheetData, evalContext).validationActive
      : control.required === true;
    if (required && isEmpty(sheetData[control.qname])) {
      errors[control.qname] = `${control.qname} is required`;
    }
  }
  return errors;
}

function checkPatternErrors(
  sheetData: Record<string, any>
): Record<string, string> {
  const errors: Record<string, string> = {};
  for (const [key, pattern] of PATTERN_BY_KEY) {
    const value = sheetData[key];
    if (typeof value === 'string' && value.length > 0 && !pattern.test(value)) {
      errors[key] = `${key} has invalid format`;
    }
  }
  return errors;
}

export const role7AuditorsReportValidator: SheetValidator = {
  sheetKey: SHEET_KEYS.auditorsReport,
  // Tier 1 by primary characteristic (required-field driven). The hideWhen
  // projection reads one cross-sheet field (filingPrerequisites.TypeOfSubmission)
  // and the conditional-required pair reads same-sheet toggles — both are
  // deterministic projections, not true cross-sheet calculations, so the
  // sheet stays in Tier 1 for parity-harness classification purposes.
  tier: 1,
  validate(
    sheetData: Record<string, any>,
    context: SheetValidationContext
  ): PureSheetValidationResult {
    // Build the eval context from the two cross-sheet slices that may carry
    // the TypeOfSubmission field. Prerequisites take precedence (editor-side
    // source of truth); filingInfo is the fallback for the listing path where
    // no editor has mounted yet. See buildEvalContext() for the full rationale.
    const filingPrerequisites = context.crossSheetData?.get(
      SHEET_KEYS.filingPrerequisites
    );
    const filingInfo = context.crossSheetData?.get(SHEET_KEYS.filingInfo);
    const evalContext = buildEvalContext(filingPrerequisites, filingInfo);
    const errors: Record<string, string> = {
      ...checkRequiredErrors(sheetData, evalContext),
      ...checkPatternErrors(sheetData),
    };
    return {
      hasErrors: Object.keys(errors).length > 0,
      hasSuggestedChanges: sheetHasTriggeredWarning(
        role7Schema.controls as unknown as WarnableControl[],
        sheetData,
        buildWarningBag(
          context.crossSheetData?.get(SHEET_KEYS.filingInfo) ??
            context.crossSheetData?.get(SHEET_KEYS.filingPrerequisites) ??
            {},
          sheetData,
          // Auditors' report compares the director's-report signing date against
          // its own; nest the cross-sheet map + own key so that warning resolves.
          { sheets: context.crossSheetData, sheetKey: SHEET_KEYS.auditorsReport }
        )
      ),
      errors,
    };
  },
};
